import {
  FC,
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useState
} from "react"
import { IModalWrapper } from "@/ui/Modal"
import { createInitialModalState } from "./factories/modalFactory.ts"
import {
  GetModalParams,
  ModalType,
  ModalsContextState,
  OpenModalArgs
} from "./types.ts"

const ModalsContext = createContext<ModalsContextState | null>(null)

export const useModalsContext = () => {
  const context = useContext(ModalsContext)
  if (!context) {
    throw new Error("useModalContext must be used within a ModalProvider")
  }
  return context
}

export const ModalsProvider: FC<PropsWithChildren> = ({ children }) => {
  const [modals, setModals] = useState(() => createInitialModalState())
  const [modalParams, setModalParams] = useState<Record<string, any>>({})
  const [modalConfig, setModalConfig] = useState<Record<string, IModalWrapper>>(
    {}
  )

  const openModal = useCallback(
    <T extends ModalType>(type: T, ...args: OpenModalArgs<T>) => {
      const params = args[0]
      setModals((prev) => ({ ...prev, [type]: true }))

      if (params !== undefined) {
        setModalParams((prev: Record<string, any>) => ({
          ...prev,
          [type]: params
        }))
      }
    },
    []
  )

  const closeModal = useCallback((type: ModalType, onClose?: () => void) => {
    if (["globalError"].includes(type)) {
      return
    }

    onClose?.()
    setModals((prev) => ({ ...prev, [type]: false }))
    setModalParams((prev: Record<string, any>) => {
      const newParams = { ...prev }
      delete newParams[String(type)]
      return newParams
    })
  }, [])

  const getModalParams = useCallback(
    <T extends ModalType>(type: T): GetModalParams<T> => {
      return modalParams[String(type)] as GetModalParams<T>
    },
    [modalParams]
  )

  const getModalParamsRequired = useCallback(
    <T extends ModalType>(type: T): NonNullable<GetModalParams<T>> => {
      const params = modalParams[String(type)]
      if (params === undefined) {
        throw new Error(
          `Modal ${String(type)} requires parameters but none were provided`
        )
      }
      return params as NonNullable<GetModalParams<T>>
    },
    [modalParams]
  )

  const setModalConfigCallback = useCallback(
    <T extends ModalType>(type: T, config: IModalWrapper) => {
      setModalConfig((prev) => ({ ...prev, [type]: config }))
    },
    [modalConfig]
  )

  const getModalConfig = useCallback(
    <T extends ModalType>(type: T): IModalWrapper => {
      return modalConfig[String(type)]
    },
    [modalConfig]
  )

  const closeAllModals = useCallback(() => {
    setModals(() => {
      return {} as any
    })
  }, [])

  const contextValue: ModalsContextState = {
    modals,
    modalConfig,
    openModal,
    closeModal,
    closeAllModals,
    getModalParams,
    getModalParamsRequired,
    setModalConfig: setModalConfigCallback,
    getModalConfig
  }

  return (
    <ModalsContext.Provider value={contextValue}>
      {children}
    </ModalsContext.Provider>
  )
}
